import { Typography } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import kebabCase from 'lodash-es/kebabCase';
import type { MouseEvent } from 'react';
import { forwardRef } from 'react';
import { useNavigate } from 'react-router';

import { BloomLink } from '@jane/bloom/components/bloomLink';
import { CategoryIcon } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/categoryIcon';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackMenuTap } from '@jane/bloom/services/tracking/track';

import styles from './categoryLink.module.css';

interface CategoryLinkProps {
  category: string;
  defaultCategory: string;
  label: string;
  reservationMode?: 'pickup' | 'delivery';
  selected?: boolean;
}

export const CategoryLink = forwardRef<HTMLAnchorElement, CategoryLinkProps>(
  function CategoryLink(
    { category, defaultCategory, label, reservationMode, selected = false },
    ref
  ) {
    const isBrandSpecials = category === 'brand_specials';
    const { featureFlags } = useRootLoaderData();
    const navigate = useNavigate();
    const urlSafeCategory = kebabCase(category);

    const handleClick = async (event: MouseEvent) => {
      event.preventDefault();
      trackMenuTap(
        {
          defaultOption: defaultCategory,
          menuOptionSelected: category,
          reservationMode,
        },
        featureFlags
      );
      navigate(isBrandSpecials ? `/jane-gold` : `/${urlSafeCategory}`);
    };

    return (
      <BloomLink
        to={isBrandSpecials ? `/jane-gold` : `/${urlSafeCategory}`}
        ref={ref}
        onClick={handleClick}
        className={clsx(styles['link'], {
          [styles['link--selected']]: selected,
        })}
        data-selected={selected}
      >
        {isBrandSpecials && selected ? (
          <div className={styles['icon--brand-specials-selected']}>
            <CategoryIcon category={category} selected={true} />
          </div>
        ) : (
          <CategoryIcon category={category} selected={selected} />
        )}
        <Typography
          color="inherit"
          maxLines={1}
          variant={selected ? 'body-bold' : 'body'}
          mt={4}
        >
          {label}
        </Typography>
      </BloomLink>
    );
  }
);
